import { TAU, clamp, normalizePoint, splinePoints } from './math.js';
const finite=(value,label)=>{if(!Number.isFinite(value))throw new TypeError(label+' must be finite.');return value;};
const pressure=value=>{finite(value,'pressure');if(value<0)throw new RangeError('pressure must be nonnegative.');return value;};
const shortestAngle=(start,end)=>((end-start+Math.PI)%TAU+TAU)%TAU-Math.PI;
/** Geometry objects hold their owning engine; instances never share draw state. */
export function geometryClasses(owner) {
  class Polygon {
    constructor(points) {
      if(!Array.isArray(points)||points.length<3)throw new Error('Polygon requires at least three vertices.');
      this.a=points.map(normalizePoint);this.vertices=this.a.map(([x,y])=>({x,y}));
      this.sides=this.vertices.map((v,i)=>[v,this.vertices[(i+1)%this.vertices.length]]);
    }
    show(){owner._shape([this.a]);return this;}
    draw(name,color,weight){owner._withStyle(()=>{if(name)owner.set(name,color,weight);owner._strokePath(this.a,true);});return this;}
    fill(color,opacity){owner._withStyle(()=>{if(color!==undefined&&color!==false)owner.fill(color,opacity);owner._fillPaths([this.a],'fill');});return this;}
    wash(color,opacity){owner._withStyle(()=>{if(color!==undefined&&color!==false)owner.wash(color,opacity);owner._fillPaths([this.a],'wash');});return this;}
    hatch(){owner._hatchPaths([this.a]);return this;}
    mass(){owner._massPaths([this.a]);return this;}
    translate(x,y){finite(x,'x');finite(y,'y');for(const p of this.a){p[0]+=x;p[1]+=y;}for(const v of this.vertices){v.x+=x;v.y+=y;}return this;}
  }
  class Plot {
    constructor(type='curve'){if(!['curve','segments'].includes(type))throw new TypeError('Plot type must be curve or segments.');this.type=type;this.segments=[];this.angles=[];this.pres=[];this.length=0;this.dir=0;this.origin=null;this._points=null;}
    addSegment(angle=0,length=0,value=1,degrees=false){
      finite(angle,'angle');finite(length,'segment length');pressure(value);
      if(length<0)throw new RangeError('Segment length must be nonnegative.');
      if(this._points)throw new Error('A sampled path cannot be extended with angular segments.');
      this.angles.push(degrees?angle*Math.PI/180:owner._radians(angle));
      this.pres[this.segments.length]=value;this.pres.length=this.segments.length+1;
      this.segments.push(length);this.length+=length;this.endAngle=undefined;return this;
    }
    endPlot(angle=0,value=1,degrees=false){finite(angle,'angle');pressure(value);this.endAngle=degrees?angle*Math.PI/180:owner._radians(angle);this.pres[this.segments.length]=value;this.pres.length=this.segments.length+1;return this;}
    rotate(angle){this.dir=owner._radians(finite(angle,'angle'));return this;}
    _path(x=0,y=0,scale=1){
      finite(x,'x');finite(y,'y');finite(scale,'scale');if(scale<=0)throw new RangeError('Plot scale must be positive.');
      if(this._points){const c=Math.cos(this.dir),s=Math.sin(this.dir);return this._points.map(p=>[(p[0]*c+p[1]*s)*scale+x,(-p[0]*s+p[1]*c)*scale+y,p[2]]);}
      const points=[[x,y,this.pres[0]??1]];
      for(let i=0;i<this.segments.length;i++){
        const start=this.angles[i]+this.dir,end=(this.angles[i+1]??this.endAngle??this.angles[i])+this.dir;
        const delta=shortestAngle(start,end);
        if(!this.segments[i])continue;
        const count=this.type==='curve'?Math.max(1,Math.ceil(this.segments[i]*scale/6)):1;
        if(points.length+count>65537)throw new RangeError('A Plot supports at most 65536 sampled segments.');
        const step=this.segments[i]*scale/count;
        for(let j=0;j<count;j++){
          const t=(j+.5)/count,angle=start+(this.type==='curve'?delta*t:0);
          x+=Math.cos(angle)*step;y-=Math.sin(angle)*step;
          const fraction=(j+1)/count;
          points.push([x,y,(this.pres[i]??1)*(1-fraction)+(this.pres[i+1]??this.pres[i]??1)*fraction]);
        }
      }
      return points;
    }
    draw(x=0,y=0,scale=1){if(this.origin){[x,y]=this.origin;scale=1;}owner._strokePath(this._path(x,y,scale));return this;}
    show(x=0,y=0,scale=1){if(this.origin){[x,y]=this.origin;scale=1;}owner._shape([this._path(x,y,scale)],false);return this;}
    genPol(x=0,y=0,scale=1){return new Polygon(this._path(x,y,scale));}
    fill(x=0,y=0,scale=1){owner._fillPaths([this._path(x,y,scale)],'fill');return this;}
    wash(x=0,y=0,scale=1){owner._fillPaths([this._path(x,y,scale)],'wash');return this;}
    hatch(x=0,y=0,scale=1){owner._hatchPaths([this._path(x,y,scale)]);return this;}
    mass(x=0,y=0,scale=1){owner._massPaths([this._path(x,y,scale)]);return this;}
    _at(distance){
      distance=clamp(finite(distance,'distance'),0,this.length);
      let at=0;
      for(let i=0;i<this.segments.length;i++){
        const length=this.segments[i];
        if(length>0&&(distance<at+length||i===this.segments.length-1))return [i,clamp((distance-at)/length)];
        at+=length;
      }
      return [Math.max(0,this.segments.length-1),1];
    }
    angle(distance){const [i,t]=this._at(distance),start=this.angles[i]??0,end=this.angles[i+1]??this.endAngle??start;return owner._fromRadians(start+(this.type==='curve'?shortestAngle(start,end)*t:0)+this.dir);}
    pressure(distance){const [i,t]=this._at(distance),first=this.pres[i]??1,last=this.pres[i+1]??first;return first+(last-first)*t;}
  }
  class Position {
    constructor(x=0,y=0){this.x=finite(x,'x');this.y=finite(y,'y');this.plotted=0;}
    isInCanvas(){const p=owner._screenPoint(this.x,this.y);return p[0]>=0&&p[0]<=owner.width&&p[1]>=0&&p[1]<=owner.height;}
    angle(){return owner._fromRadians(owner._fieldAngle(this.x,this.y));}
    _advance(angle,length,step,scale=1){
      finite(length,'length');finite(step,'step');finite(scale,'scale');
      if(length<0||scale<=0||(length>0&&step<=0))throw new RangeError('Length must be nonnegative; step and scale must be positive.');
      if(!length)return this;
      const count=Math.ceil(length/step);if(count>100000)throw new RangeError('Position movement supports at most 100000 steps.');
      let moved=0;
      for(let i=0;i<count;i++){
        const distance=Math.min(step,length-moved),a=angle()+owner._fieldAngle(this.x,this.y);
        this.x+=Math.cos(a)*distance;this.y-=Math.sin(a)*distance;this.plotted+=distance/scale;moved+=distance;
      }
      return this;
    }
    moveTo(angle,length,step=length){const a=owner._radians(finite(angle,'angle'));return this._advance(()=>a,length,step);}
    plotTo(plot,length,step=length,scale=1){if(typeof plot?.angle!=='function')throw new TypeError('plotTo requires a Plot.');return this._advance(()=>owner._radians(plot.angle(this.plotted)),length,step,scale);}
    clone(){const copy=new Position(this.x,this.y);copy.plotted=this.plotted;return copy;}
  }
  return {Polygon,Plot,Position};
}
export function pathPlot(owner,points){
  if(!Array.isArray(points)||points.length<2)throw new Error('A sampled path requires at least two points.');
  const plot=new owner.Plot('segments'),vertices=points.map(normalizePoint);
  for(let i=1;i<vertices.length;i++){
    const a=vertices[i-1],b=vertices[i];
    plot.addSegment(Math.atan2(a[1]-b[1],b[0]-a[0])*180/Math.PI,Math.hypot(b[0]-a[0],b[1]-a[1]),a[2],true);
  }
  plot.endPlot((plot.angles.at(-1)??0)*180/Math.PI,vertices.at(-1)[2],true);plot._points=vertices;return plot;
}
export { splinePoints };
